import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import * as Icons from "@mui/icons-material";
import { Tooltip, IconButton, Avatar, Button } from "@mui/material";
import { useSession, signOut } from "next-auth/react";
import { deepOrange } from "@mui/material/colors";
import {
  clearUserDetail,
} from "../../../redux/slices/user";
import { useDispatch } from "react-redux";

// helper: email to show on avatar
const getStored = (k) => {
  if (typeof window === "undefined") return "";
  const v = sessionStorage.getItem(k);
  try { return v ? JSON.parse(v) : ""; } catch { return v || ""; }
};

const getMaxLength = () => {
  const width = window.innerWidth;

  if (width < 480) return 25;      // small screens
  if (width < 769) return 30;      // tablets / medium devices
  if (width < 1025) return 15;     // small laptops
  return 20;                       // large screens
};

const truncate = (text) => {
  if (!text) return "";
  const MAX_LENGTH = getMaxLength();
  return text.length > MAX_LENGTH ? text.slice(0, MAX_LENGTH) + "..." : text;
};


const Sidebar = ({ enableClos = false }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { data: session } = useSession();
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isSignInUpCompleted, setIsSignInUpCompleted] = useState(
    () => (typeof window !== "undefined" ? localStorage.getItem("isSignInUpCompleted") : "false")
  );

  const displayEmail =
    session?.customuser?.responseData?.email ||
    session?.customuser?.email ||
    session?.user?.email ||
    getStored("userEmail");

  const initials = displayEmail ? displayEmail.slice(0, 1).toUpperCase() : "U";
  const isLoggedIn = isSignInUpCompleted === "true" && !!displayEmail;

  const safeParse = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return value; // return raw if not JSON
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      setUserName(safeParse(sessionStorage.getItem("userName")));
      setUserEmail(safeParse(sessionStorage.getItem("userEmail")));
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updated = localStorage.getItem("isSignInUpCompleted");
    if (updated) setIsSignInUpCompleted(updated);
  }, [typeof window !== "undefined" ? localStorage.getItem("isSignInUpCompleted") : ""]);

  const sidebarContent = [
    {
      id: 2,
      icon: "Description",
      name: "Booking History",
      routePath: "/rsldashboard/bookinglist",
    },
    {
      id: 5,
      icon: "CreditCard",
      name: "Card Details",
      routePath: "/rsldashboard/db-cardDetails",
    },
    {
      id: 4,
      icon: "Settings",
      name: "Settings",
      routePath: "/rsldashboard/db-settings",
    }
  ];

  const logoutBtn = {
    icon: "Logout",
    label: "Log out",
  };

  const handleSessionClear = () => {
    sessionStorage.removeItem("rslkey");
    sessionStorage.removeItem("rslkeyId");
    sessionStorage.removeItem("userEmail");
    sessionStorage.removeItem("userName");
    sessionStorage.removeItem("countryCode");
    sessionStorage.removeItem("phone");
    sessionStorage.removeItem("isSig");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  };

  const handleLogout = async () => {
    handleSessionClear();
    dispatch(clearUserDetail());
    localStorage.setItem("isSignInUpCompleted", "false");
    await signOut({ redirect: router.asPath.includes("rsldashboard"), callbackUrl: "/" });
    window.location.replace("/");
  };

  return (
    <div className="sidebar-container">

      {/* USER CARD */}
      <div className="user-card">
        {/* <Image
          src="/img/avatars/testimonials/3.png"
          width={60}
          height={60}
          alt="User"
          className="user-avatar"
        /> */}

        <Tooltip title={displayEmail || ""}>
          <IconButton size="small" sx={{ ml: 1 }}>
            <Avatar sx={{ bgcolor: deepOrange[500], textTransform: "uppercase" }}>
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        <div className="user-info">
          <Tooltip title={userName}>
            <h4 className="username" title={userName}>
              {truncate(userName)}
            </h4>
          </Tooltip>
          <Tooltip title={userEmail}>
            <p className="user-email" title={userEmail}>
              {truncate(userEmail)}
            </p>
          </Tooltip>
        </div>
      </div>

      {/* Divider */}
      <div className="sidebar-divider"></div>

      {/* MENU */}
      <div className="sidebar-menu">
        {sidebarContent.map((item) => {
          const IconComp = Icons[item.icon];
          const isActive = router.asPath === item.routePath;

          return (
            <div key={item.id}>
              <Link
                href={item.routePath}
                className={`menu-item ${isActive ? "active" : ""}`}
              >
                <IconComp className="menu-icon" />
                <span>{item.name}</span>
              </Link>

              {/* Divider only after Card Details */}
              {item.id === 5 && <div className="sidebar-divider" style={{
                margin: "16px 0px"
              }}></div>}
            </div>
          );
        })}
      </div>

      {/* LOGOUT BUTTON */}
      <div className="logout-section d-flex justify-between">
        <button className="logout-btn" onClick={handleLogout}>
          <Icons.Logout className="logout-icon" />
          {logoutBtn.label}
        </button>
        {
          enableClos &&
          <Button style={{ color: "gray" }} data-bs-dismiss="offcanvas">Close</Button>
        }
      </div>
    </div>
  );
};

export default Sidebar;
